import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import type { KeywordSuggestion } from "@/resources/keyword-theme";
import type { Keyword } from "@/types/keyword";
import { KeywordSuggestionTable } from "./KeywordThemeShared";

interface AddKeywordSignalAiTabProps {
  themeName: string;
  isGenerating: boolean;
  aiSuggestions: KeywordSuggestion[] | null;
  keywordById: Map<number, Keyword>;
  selectedIds: Set<number>;
  onToggleId: (id: number) => void;
  onAskAI: () => void;
  onSwitchToPick: () => void;
}

export function AddKeywordSignalAiTab({
  themeName,
  isGenerating,
  aiSuggestions,
  keywordById,
  selectedIds,
  onToggleId,
  onAskAI,
  onSwitchToPick,
}: AddKeywordSignalAiTabProps) {
  if (isGenerating) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          Signal AI is finding the best keywords for this theme…
        </p>
      </div>
    );
  }

  if (aiSuggestions !== null) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        {aiSuggestions.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
            <p className="text-sm text-muted-foreground">
              No keyword suggestions found for this theme.
            </p>
            <Button variant="outline" size="sm" onClick={onSwitchToPick}>
              Pick keywords manually →
            </Button>
          </div>
        ) : (
          <KeywordSuggestionTable
            rows={aiSuggestions.map((s) => {
              const kw = keywordById.get(s.keyword_id);
              return {
                key: s.keyword_id,
                label: s.keyword,
                rank: kw?.rank ?? null,
                kps: kw?.kps ?? null,
                searchVolume: kw?.search_volume ?? null,
                relevance: kw?.hero_relevance ?? null,
                trend: kw?.search_volume_trend ?? null,
                selected: selectedIds.has(s.keyword_id),
                onSelect: () => onToggleId(s.keyword_id),
              };
            })}
          />
        )}
        <div className="border-t px-6 py-2 shrink-0 flex items-center justify-between">
          <button
            onClick={onAskAI}
            disabled={isGenerating}
            className="text-xs text-primary hover:underline flex items-center gap-1"
          >
            <Sparkles className="h-3 w-3" />
            Re-generate suggestions
          </button>
          <button
            onClick={onSwitchToPick}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Pick manually instead →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6">
      <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
        <Sparkles className="h-3 w-3 shrink-0" />
        Signal AI
      </div>
      <h3 className="text-base font-semibold">Find keywords for this theme</h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        Signal AI will scan your keyword bank and suggest the best
        matches for the &ldquo;{themeName}&rdquo; theme.
      </p>
      <Button onClick={onAskAI} className="mt-2 gap-1.5">
        <Sparkles className="h-4 w-4" />
        Ask Signal AI
      </Button>
      <button
        onClick={onSwitchToPick}
        className="text-xs text-muted-foreground hover:text-foreground"
      >
        Pick keywords manually →
      </button>
    </div>
  );
}
